import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProfileSelection } from 'src/app/shared/models';

@Injectable()
export class SelectionService {
  private selection: ProfileSelection = {
    type: 'none',
    allProfiles: false,
    range: null,
    ids: null,
  };

  private updateCallback: () => void | null = null;
  private pageSelectionCallback: (setToSelected: boolean) => void | null = null;
  private callbackIsActive = false;
  private profileSelectionSrc$ = new BehaviorSubject<ProfileSelection>(this.selection);
  readonly profileSelection$: Observable<ProfileSelection> =
    this.profileSelectionSrc$.asObservable();

  getSelection(): ProfileSelection {
    return this.selection;
  }

  isProfileSelected(id: string, profileRangePosition: number): boolean {
    switch (this.selection.type) {
      case 'all':
        return true;
      case 'ids':
        return this.selection.ids.has(id);
      case 'range':
        return (
          profileRangePosition >= this.selection.range.from &&
          profileRangePosition <= this.selection.range.to
        );
      default:
        return false;
    }
  }

  reset(triggerUpdate = true): void {
    this.selection = {
      type: 'none',
      allProfiles: false,
      range: null,
      ids: null,
    };

    if (triggerUpdate) {
      this.emitUpdate();
    }
  }

  selectProfile(id: string, selected: boolean): void {
    if (!this.selection.ids) {
      this.reset(false);
      this.selection.type = 'ids';
      this.selection.ids = new Set();
    }

    if (selected) {
      this.selection.ids.add(id);
    } else {
      this.selection.ids.delete(id);
    }

    this.emitUpdate();
  }

  selectRange(from: number, to: number): void {
    this.reset(false);
    this.selection.type = 'range';
    this.selection.range = { from, to };
    this.emitUpdate();
  }

  selectAll(): void {
    this.reset(false);
    this.selection.type = 'all';
    this.selection.allProfiles = true;
    this.emitUpdate();
  }

  requestThisPageSelection(resetAlreadySelected: boolean, select: boolean): void {
    if (resetAlreadySelected) {
      this.reset(false);
    }

    this.emitPageSelection(select);
    this.emitUpdate();
  }

  setUpdateCallback(callback: () => void): void {
    this.updateCallback = callback;
  }

  setPageSelectionCallback(callback: (setToSelected: boolean) => void): void {
    this.pageSelectionCallback = callback;
  }

  /** SelectionService don't keep track of number for select all so totalProfiles needs to be provided. */
  getSelectedTotal(totalProfiles: number): number {
    switch (this.selection.type) {
      case 'all':
        return totalProfiles;
      case 'range':
        return this.selection.range.to - this.selection.range.from + 1;
      case 'ids':
        return this.selection.ids.size;
      default:
        return 0;
    }
  }

  private emitUpdate(): void {
    if (this.callbackIsActive) return;

    this.profileSelectionSrc$.next({ ...this.selection });

    this.callbackIsActive = true;
    this.updateCallback?.();
    this.callbackIsActive = false;
  }

  private emitPageSelection(setToSelected: boolean): void {
    if (this.callbackIsActive) return;

    this.callbackIsActive = true;
    this.pageSelectionCallback?.(setToSelected);
    this.callbackIsActive = false;
  }
}
